import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ComplianceMatrixSnapshot } from './types';
import { matrixService } from './matrixService';

interface Props {
  snapshots: ComplianceMatrixSnapshot[];
  onClose: () => void;
  onSnapshotCreated: () => void;
}

export const ComplianceMatrixSnapshotModal: React.FC<Props> = ({ snapshots, onClose, onSnapshotCreated }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [snapshotCode, setSnapshotCode] = useState(`SNAP-${new Date().getFullYear()}-${Date.now().toString(36).toUpperCase()}`);
  const [snapshotTitle, setSnapshotTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await matrixService.createSnapshot({
        snapshot_code: snapshotCode,
        snapshot_title: snapshotTitle,
        frozen_by: 'Canan Ak (Compliance Monitoring Manager)',
        notes: notes || undefined
      });
      setShowCreate(false);
      setSnapshotTitle('');
      setNotes('');
      onSnapshotCreated();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Uyum Matrisi Dondurulmus Anlik Verileri (Snapshots)</h3>
            <p>
              Belirli bir tarihteki genel uyum ve icra durumunu kalici olarak saklayan dondurulmus kayitlar
            </p>
          </div>
          <button onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">Mevcut Dondurulmus Anlik Viewr ({snapshots.length})</span>
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              {showCreate ? 'Cancel' : '+ Yeni Durum Dondur'}
            </button>
          </div>

          {showCreate && (
            <form onSubmit={handleCreateSubmit} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block font-medium text-gray-700">Goruntu Kodu</label>
                  <input
                    type="text"
                    value={snapshotCode}
                    onChange={(e) => setSnapshotCode(e.target.value)}
                    className="w-full mt-1 p-1.5 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700">Baslik / Tanim</label>
                  <input
                    type="text"
                    placeholder="Orn: 2026 Q1 EASA Denetim Oncesi Durum"
                    value={snapshotTitle}
                    onChange={(e) => setSnapshotTitle(e.target.value)}
                    className="w-full mt-1 p-1.5 border rounded"
                    required
                  />
                </div>
              </div>
              <div className="text-xs">
                <label className="block font-medium text-gray-700">Aciklama / Notlar</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full mt-1 p-1.5 border rounded"
                  placeholder="Dondurma sebebi ve kapsam notlari..."
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 transition"
                >
                  {loading ? 'Donduruluyor...' : 'Anlik Goruntuyu Dondur & Sakla'}
                </button>
              </div>
            </form>
          )}

          {snapshots.length === 0 ? (
            <div className="text-center py-8 text-xs text-gray-400">
              Henuz dondurulmus anlik goruntu bulunmamaktadir.
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.map((s) => (
                <div key={s.id} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-gray-900 text-sm">{s.snapshot_title}</div>
                      <div className="text-xs text-gray-500">Kod: {s.snapshot_code} • Tarih: {s.snapshot_date}</div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-extrabold text-blue-600">%{s.overall_compliance_rate}</span>
                      <div className="text-xs text-gray-400">Genel Uyum Orani</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 pt-2 border-t border-gray-100 text-xs">
                    <div>Toplam Madde: <b>{s.total_entries}</b></div>
                    <div className="text-emerald-700">Tam Uyumlu: <b>{s.compliant_count}</b></div>
                    <div className="text-amber-700">Kismi Uyumlu: <b>{s.partially_compliant_count}</b></div>
                    <div className="text-red-700">Uyumsuz: <b>{s.non_compliant_count}</b></div>
                  </div>

                  {s.notes && (
                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      <b>Not:</b> {s.notes}
                    </div>
                  )}

                  <div className="text-[11px] text-gray-400">
                    Donduran Yetkili: {s.frozen_by}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
